import typeDefaults from '@/common/constants/type-defaults'
import { cloneTableValueForSetField, isTableLikeValueForSetField } from '@/common/utils/cloneTableValue'
import { registerUniDataToPlain, setType } from '@/common/utils/typedValueSerializer'
import { unwrapScriptValue, wrapScriptValue } from '@/common/utils/wrapScriptValue'
import UniDataTable from './UniDataTable'
import UniDate from './UniDate'
import {TypeSerializer} from "@/common/utils/typeSerializer.ts";

interface TypedValue {
  value: any
  type?: string
}

/**
 * Класс UniData
 */
export default class UniData {
  private data: Record<string, TypedValue>

  constructor(data: Record<string, any> = {}) {
    this.data = {}
    registerUniDataToPlain(this, () => ({ ...this.data }))
    this.add(data)
  }

  private getDefaultValueForFieldType(type?: unknown): any {
    const normalizedType = typeof type === 'string' ? type : String(type ?? '')
    switch (normalizedType) {
      case 'UniDataTable':
      case 'UniDataTableFields':
        return { rows: [] }
      case 'Boolean':
        return false
      case 'Long':
      case 'Double':
      case 'BigDecimal':
      case 'Integer':
      case 'Float':
        return null
      default:
        return ''
    }
  }

  private normalizeField(field: any): any {
    if (!field || typeof field !== 'object' || !('type' in field)) {
      return field
    }

    if (!('value' in field) || typeof (field as any).value === 'undefined') {
      return {
        ...(field as any),
        value: this.getDefaultValueForFieldType((field as any).type),
      }
    }

    return field
  }

  private normalizeFieldsMap(obj: Record<string, any>): Record<string, any> {
    return Object.fromEntries(
      Object.entries(obj || {}).map(([key, value]) => [key, this.normalizeField(value)]),
    )
  }

  add(obj: Record<string, any> | UniData, _fieldIndex?: number): this {
    const src: Record<string, any> =
      obj instanceof UniData ? (obj as UniData)._getAllFields() : (obj as Record<string, any>)
    Object.assign(this.data, this.normalizeFieldsMap(src))
    return this
  }

  /**
   * Заменяет данные
   */
  replace(obj: Record<string, any>, _old?: any): void {
    Object.assign(this.data, this.normalizeFieldsMap(obj))
  }

  /**
   * Проверяет наличие поля
   */
  hasField(name: string): boolean {
    return this.resolveField(name) !== undefined
  }

  /**
   * Проверяет наличие таблицы
   */
  hasTable(name: string): boolean {
    const field = this.resolveField(name)
    return (
      field !== undefined &&
      (field.type === 'UniDataTable' || field.type === 'UniDataTableFields')
    )
  }

  isTableField(name: string): boolean {
    return this.hasTable(name)
  }

  getFields(): { fieldsArray: () => string[] } {
    const self = this
    return {
      fieldsArray(): string[] {
        const top = Object.keys(self.data)
        const nestedFields = (self.data as any).fields?.fields
        const nested =
          nestedFields && typeof nestedFields === 'object' && !Array.isArray(nestedFields)
            ? Object.keys(nestedFields)
            : []
        return [...new Set([...top, ...nested])]
      },
    }
  }

  /**
   * Устанавливает значение поля
   */
  setField(name: string, value: any): this {
    const toStore = isTableLikeValueForSetField(value) ? cloneTableValueForSetField(value) : value
    this.data[name] = setType(toStore)
    return this
  }

  setFieldAsObject(name: string, value: any): this {
    return this.setField(name, value)
  }

  /**
   * Очищает поле
   */
  empty(name: string): void {
    if (this.data[name]) {
      this.data[name].value = typeDefaults[this.data[name].type as keyof typeof typeDefaults]
    }
  }

  /**
   * Добавляет .size() всем полям-таблицам и массивам в data (для скриптов в стиле Java items.size()).
   * Рекурсивно обходит вложенные объекты (fields.fields и т.д.), т.к. при refill скрипт может брать items из merge/getForm.
   */
  ensureTableValuesHaveSize(): void {
    const addSize = (val: any): void => {
      if (val == null) return
      if (typeof (val as any).size === 'function') return
      if (Array.isArray(val)) {
        ;(val as any).size = () => (val as unknown[]).length
        return
      }
      if (typeof val === 'object' && Array.isArray((val as any).rows)) {
        ;(val as any).size = function (this: { rows: unknown[] }) {
          return this.rows?.length ?? 0
        }
      }
    }
    const walk = (obj: any, depth: number): void => {
      if (depth > 6 || obj == null || typeof obj !== 'object') return
      if (Array.isArray(obj)) {
        addSize(obj)
        obj.forEach((item) => walk(item, depth + 1))
        return
      }
      addSize(obj)
      for (const key of Object.keys(obj)) {
        const v = obj[key]
        if (v == null) continue
        if (typeof v === 'object' && 'value' in v && ('type' in v || Array.isArray((v as any).rows))) {
          addSize((v as any).value)
          walk((v as any).value, depth + 1)
        }
        walk(v, depth + 1)
      }
    }
    walk(this.data, 0)
  }

  /**
   * Находит поле по имени: сначала в this.data, затем в this.data.fields.fields (вложенная структура при merge).
   */
  private resolveField(name: string): TypedValue | undefined {
    if (this.data[name] !== undefined) return this.data[name] as TypedValue
    const nested = (this.data as any).fields?.fields?.[name]
    if (nested !== undefined) return nested as TypedValue
    return undefined
  }

  /**
   * `add()` may store a plain scalar (TEXT getData) or `{ type, value }`.
   * Readers must accept both — formatPostalAddress uses getFieldAsString.
   */
  private readStoredValue(field: unknown): unknown {
    if (field == null) return field
    if (typeof field !== 'object' || Array.isArray(field)) return field
    if ('value' in (field as object)) return (field as TypedValue).value
    return undefined
  }

  /**
   * Оборачивает значение для Rhino/Java-скриптов (.size(), .length()).
   * Не мутирует исходный объект, чтобы корректно работать с Vue reactive.
   */
  static wrapValueWithSize(value: any): any {
    return wrapScriptValue(value)
  }

  /**
   * Получает поле (сначала плоский ключ, затем data.fields.fields[name]).
   * Для значений-таблиц и массивов возвращает обёртку с .size() (совместимость со скриптами items.size()).
   */
  getField(name: string): TypedValue {
    const field = this.resolveField(name)
    if (field === undefined) {
      console.warn(`UniData: field ${name} not found`)
      return { value: undefined }
    }
    const value = this.readStoredValue(field)
    const wrapped = UniData.wrapValueWithSize(value)
    if (
      field &&
      typeof field === 'object' &&
      !Array.isArray(field) &&
      'value' in (field as object) &&
      wrapped === value
    ) {
      return field
    }
    return { ...(typeof field === 'object' && field && !Array.isArray(field) ? field : {}), value: wrapped }
  }

  /**
   * Получает поле как строку
   */
  getFieldAsString(name: string, def: string = ''): string {
    const field = this.resolveField(name)
    if (field === undefined) return def
    const v = unwrapScriptValue(this.readStoredValue(field))
    if (v == null) return def
    if (
      typeof v === 'object' &&
      !Array.isArray(v) &&
      typeof (v as { __str?: unknown }).__str === 'string'
    ) {
      return (v as { __str: string }).__str
    }
    if (typeof v === 'object') return def
    return String(v)
  }

  /**
   * Получает поле как число.
   * Скаляр 0 (числовой code ListBox) — валидное значение, не отсутствие поля.
   */
  getFieldAsLong(name: string, def: number = 0): number {
    const field = this.resolveField(name)
    if (field === undefined) return def
    const v = unwrapScriptValue(this.readStoredValue(field))
    if (v == null || v === '') return def
    const n = Number(v)
    return Number.isFinite(n) ? n : def
  }

  /**
   * Получает поле как булево значение
   */
  getFieldAsBoolean(name: string, def: boolean = false): boolean {
    const field = this.resolveField(name)
    if (field === undefined) return def
    // Без wrapScriptValue: Proxy от строки '0' даёт Boolean(proxy)=true и ломает
    // onInlineMenuShow вида !getFieldAsBoolean("group") (форма 2673 / TreeTable).
    const v = unwrapScriptValue(this.readStoredValue(field))
    if (typeof v === 'boolean') return v
    if (v === 1 || v === '1') return true
    if (v === 0 || v === '0') return false
    if (typeof v === 'string') {
      const t = v.trim().toLowerCase()
      if (t === 'true') return true
      if (t === 'false') return false
    }
    return Boolean(v)
  }

  /**
   * Получает поле как дату
   */
  getFieldAsDate(name: string, def: UniDate | null = null): UniDate | null {
    const field = this.resolveField(name)
    if (field === undefined) {
      return def
    }
    const raw = this.readStoredValue(field)
    const date = new UniDate(raw instanceof Date || typeof raw === 'string' ? raw : undefined)
    return date.isNull ? def : date
  }

  /**
   * Получает поле как таблицу
   */
  getFieldAsTable(name: string, def: UniDataTable | null = null): UniDataTable {
    const resolved = this.resolveField(name)
    if (resolved === undefined) {
      return def != null ? (def instanceof UniDataTable ? def : new UniDataTable(def as any)) : new UniDataTable()
    }
    let value = resolved.value
    if (value == null && def != null) {
      value = def
    }
    if (value instanceof UniDataTable) {
      return value
    }
    this.setField(name, new UniDataTable(value ?? { rows: [] }))
    return this.resolveField(name)!.value as UniDataTable
  }

  /**
   * Получает поле как объект
   */
  getFieldAsObject(name: string, def: any = null): any {
    return this.resolveField(name) ? this.getField(name).value : def
  }

  /**
   * Переименовывает поле
   */
  translateField(from: string, to: string): void {
    this.data[to] = this.data[from]
    delete this.data[from]
  }

  /**
   * Удаляет поле
   */
  removeField(name: string): void {
    delete this.data[name]
  }

  _getAllFields(): Record<string, TypedValue> {
    return this.data
  }

  private static isEmptyUniDataTableValue(raw: unknown): boolean {
    if (raw == null || typeof raw !== 'object') return false
    const table = raw as { rows?: unknown[]; empty?: boolean }
    return (
      Array.isArray(table.rows) &&
      table.rows.length === 0 &&
      (table.empty === true || table.rows.length === 0)
    )
  }

  private static formatGetDataScalar(raw: unknown): string {
    if (raw === true || raw === 1 || raw === '1' || raw === 'true') return '1'
    if (raw === false || raw === 0 || raw === '0' || raw === 'false') return '0'
    if (raw == null) return ''
    return String(raw)
  }

  private static formatGetDataFieldLine(fname: string, raw: unknown, nestedColumns?: string[]): string {
    if (UniData.isEmptyUniDataTableValue(raw)) {
      return `'${fname}' => empty`
    }
    if (raw != null && typeof raw === 'object' && Array.isArray((raw as { rows?: unknown }).rows)) {
      const nested = raw as {
        rows?: { fields?: Record<string, { value?: unknown }> }[]
        columns?: string[]
      }
      const childCols = nested.columns?.length ? nested.columns : nestedColumns
      const lines: string[] = [`'${fname}' => '`]
      lines.push('  [')
      for (const nrow of nested.rows ?? []) {
        if (nrow?.fields) {
          lines.push(UniData.formatGetDataRowFields(nrow.fields, childCols))
        }
      }
      lines.push("  ]'")
      return lines.join('\n')
    }
    return `'${fname}' => '${UniData.formatGetDataScalar(raw)}'`
  }

  private static formatGetDataRowFields(
    fields: Record<string, { value?: unknown }>,
    columnOrder?: string[],
  ): string {
    const lines: string[] = ['    {']
    const orderedKeys: string[] = []
    if (columnOrder?.length) {
      for (const col of columnOrder) {
        if (!orderedKeys.includes(col)) orderedKeys.push(col)
      }
      for (const key of Object.keys(fields)) {
        if (!orderedKeys.includes(key)) orderedKeys.push(key)
      }
    } else {
      orderedKeys.push(...Object.keys(fields))
    }

    for (const fname of orderedKeys) {
      const fval = fields[fname]
      if (fval === undefined) {
        lines.push(`'${fname}' => empty`)
        continue
      }
      lines.push(UniData.formatGetDataFieldLine(fname, fval?.value, columnOrder))
    }
    lines.push('    }')
    return lines.join('\n')
  }

  private static formatGetDataTableValue(tableValue: {
    rows?: { fields?: Record<string, { value?: unknown }> }[]
    columns?: string[]
  }): string {
    const columns = tableValue.columns ?? []
    const lines: string[] = ['  [']
    for (const row of tableValue.rows ?? []) {
      if (row?.fields) {
        lines.push(UniData.formatGetDataRowFields(row.fields, columns))
      }
    }
    lines.push('  ]')
    return lines.join('\n')
  }

  /** Для en.getElement(...).getData().toString() в web_client_exec. */
  toString(): string {
    const lines: string[] = ['{']
    for (const [key, field] of Object.entries(this.data)) {
      const raw = field?.value
      if (raw != null && typeof raw === 'object' && Array.isArray((raw as { rows?: unknown }).rows)) {
        lines.push(`  '${key}' => '`)
        lines.push(
          UniData.formatGetDataTableValue(
            raw as {
              rows?: { fields?: Record<string, { value?: unknown }> }[]
              columns?: string[]
            },
          ),
        )
        lines.push("  '")
      } else if (raw != null && typeof raw === 'object') {
        try {
          lines.push(`  '${key}' => '\n  ${JSON.stringify(raw, null, 2).replace(/\n/g, '\n  ')}'`)
        } catch {
          lines.push(`  '${key}' => '${String(raw)}'`)
        }
      } else {
        lines.push(`  '${key}' => '${raw ?? ''}'`)
      }
    }
    lines.push('}')
    return lines.join('\n')
  }

  /**
   * Выбирает поля
   */
  _select(obj: Record<string, any>): void {
    const normalized = this.normalizeFieldsMap(obj)
    Object.assign(this.data, normalized)
  }
}
